import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import api from '../services/api';
import { isAuthenticated } from '../services/auth';

function StatCard({ icon, iconBg, accentColor, label, value, sub, badge, badgeColor }) {
  return (
    <div className="stat-card">
      <div className="stat-card-accent" style={{ background: accentColor }} />
      <div className="stat-card-top">
        <span className="stat-card-icon material-symbols-outlined" style={{ background: iconBg }}>
          {icon}
        </span>
        {badge && (
          <span className="stat-card-badge" style={{ background: badgeColor?.bg, color: badgeColor?.text }}>
            {badge}
          </span>
        )}
      </div>
      <p className="stat-card-label">{label}</p>
      <p className="stat-card-value">{value}</p>
      {sub && <p className="stat-card-sub">{sub}</p>}
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    produtos: 0, clientes: 0, pedidosMes: 0, receitaMes: 0,
    ticketMedio: 0, orcPendentes: 0, variacaoReceita: null,
  });
  const [lowStock, setLowStock] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    const safe = (promise) => promise.catch(() => ({ data: [] }));
    try {
      const [prodRes, cliRes, pedRes, entRes, saidaRes, orcRes] = await Promise.all([
        safe(api.get('/produtos/',    { headers })),
        safe(api.get('/clientes/',   { headers })),
        safe(api.get('/pedidos/',    { headers })),
        safe(api.get('/entradas/',   { headers })),
        safe(api.get('/saidas/',     { headers })),
        safe(api.get('/orcamentos/', { headers })),
      ]);

      const produtos   = Array.isArray(prodRes.data)  ? prodRes.data  : [];
      const clientes   = Array.isArray(cliRes.data)   ? cliRes.data   : [];
      const pedidos    = Array.isArray(pedRes.data)   ? pedRes.data   : [];
      const entradas   = Array.isArray(entRes.data)   ? entRes.data   : [];
      const saidas     = Array.isArray(saidaRes.data) ? saidaRes.data : [];
      const orcamentos = Array.isArray(orcRes.data)   ? orcRes.data   : [];

      const now = new Date();
      const isMesAtual = (dateStr) => {
        if (!dateStr) return false;
        const d = new Date(dateStr + 'T00:00:00');
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      };
      const isMesAnterior = (dateStr) => {
        if (!dateStr) return false;
        const d = new Date(dateStr + 'T00:00:00');
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return d.getMonth() === prev.getMonth() && d.getFullYear() === prev.getFullYear();
      };

      const pedidosMes = pedidos.filter((p) => isMesAtual(p.data_pedido));
      const receitaMes = pedidosMes.reduce((s, p) => s + parseFloat(p.valor_total || 0), 0);
      const receitaAnterior = pedidos
        .filter((p) => isMesAnterior(p.data_pedido))
        .reduce((s, p) => s + parseFloat(p.valor_total || 0), 0);
      const variacaoReceita = receitaAnterior > 0
        ? Math.round(((receitaMes - receitaAnterior) / receitaAnterior) * 100)
        : null;
      const ticketMedio = pedidosMes.length > 0 ? receitaMes / pedidosMes.length : 0;
      const orcPendentes = orcamentos.filter((o) => o.status === 'Pendente').length;

      setStats({ produtos: produtos.length, clientes: clientes.length, pedidosMes: pedidosMes.length,
                 receitaMes, ticketMedio, orcPendentes, variacaoReceita });
      setLowStock(produtos.filter((p) => p.quantidade_estoque < (p.estoque_minimo ?? 5)).slice(0, 5));

      // Feed de atividade misto: últimas entradas + saídas + pedidos
      const feedEntradas = entradas.slice(0, 3).map((e) => ({ _tipo: 'entrada', _data: e.data_entrada, ...e }));
      const feedSaidas   = saidas.slice(0, 2).map((s)   => ({ _tipo: 'saida',   _data: s.data_saida,   ...s }));
      const feedPedidos  = pedidos.slice(0, 2).map((p)  => ({ _tipo: 'pedido',  _data: p.data_pedido,  ...p }));
      const feed = [...feedEntradas, ...feedSaidas, ...feedPedidos]
        .sort((a, b) => new Date(b._data + 'T00:00:00') - new Date(a._data + 'T00:00:00'))
        .slice(0, 5);
      setRecentActivity(feed);

      // Receita por mês: últimos 6 meses
      const last6 = Array.from({ length: 6 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        return { year: d.getFullYear(), month: d.getMonth(),
                 label: d.toLocaleString('pt-BR', { month: 'short' }) };
      });
      const monthlyRevenue = last6.map((m) => ({
        label: m.label,
        value: pedidos
          .filter((p) => {
            if (!p.data_pedido) return false;
            const d = new Date(p.data_pedido + 'T00:00:00');
            return d.getMonth() === m.month && d.getFullYear() === m.year;
          })
          .reduce((s, p) => s + parseFloat(p.valor_total || 0), 0),
      }));
      const maxRev = Math.max(...monthlyRevenue.map((m) => m.value), 1);
      setChartData(monthlyRevenue.map((m, i) => ({
        label: m.label,
        height: Math.max(4, Math.round((m.value / maxRev) * 100)),
        value: m.value,
        current: i === 5,
      })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const mesAtual = new Date().toLocaleString('pt-BR', { month: 'long', year: 'numeric' });

  const formatDate = (str) => {
    if (!str) return '—';
    const d = new Date(str + 'T00:00:00');
    const diff = Math.floor((new Date() - d) / 86400000);
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Ontem';
    if (diff < 7)  return `${diff} dias atrás`;
    return d.toLocaleDateString('pt-BR');
  };

  return (
    <div className="dashboard-page">

      {/* Page header */}
      <div className="dash-page-header">
        <div>
          <h2 className="dash-title">Painel Operacional</h2>
          <p className="dash-subtitle">Métricas de inventário e operações em tempo real.</p>
        </div>
        <div className="dash-header-actions">
          <button className="dash-btn-outline" onClick={() => navigate('/entradas')}>
            <span className="material-symbols-outlined">download</span>
            Entradas
          </button>
          <button className="dash-btn-primary" onClick={() => navigate('/produtos')}>
            <span className="material-symbols-outlined">inventory</span>
            Gerenciar Estoque
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="stat-grid">
        <StatCard
          icon="inventory_2"
          iconBg="#d6e4f9"
          accentColor="#cca730"
          label="Total de Produtos"
          value={loading ? '—' : stats.produtos}
          sub={loading ? '' : `${lowStock.length} em estoque crítico`}
        />
        <StatCard
          icon="people"
          iconBg="#d7e2ff"
          accentColor="#545e76"
          label="Clientes Ativos"
          value={loading ? '—' : stats.clientes}
          sub="Clientes cadastrados"
        />
        <StatCard
          icon="shopping_bag"
          iconBg="#dcfce7"
          accentColor="#15803d"
          label="Pedidos do Mês"
          value={loading ? '—' : stats.pedidosMes}
          sub={mesAtual}
        />
        <StatCard
          icon="payments"
          iconBg="#ffe088"
          accentColor="#735c00"
          label="Receita do Mês"
          value={loading ? '—' : formatCurrency(stats.receitaMes)}
          sub={loading || stats.variacaoReceita === null ? mesAtual : `${stats.variacaoReceita >= 0 ? '+' : ''}${stats.variacaoReceita}% vs mês anterior`}
          badge={(!loading && stats.variacaoReceita !== null) ? `${stats.variacaoReceita >= 0 ? '▲' : '▼'} ${Math.abs(stats.variacaoReceita)}%` : undefined}
          badgeColor={(!loading && stats.variacaoReceita !== null)
            ? (stats.variacaoReceita >= 0 ? { bg: '#dcfce7', text: '#15803d' } : { bg: '#ffdad6', text: '#ba1a1a' })
            : undefined}
        />
        <StatCard
          icon="request_quote"
          iconBg="#fce4ec"
          accentColor="#c2185b"
          label="Orçamentos Pendentes"
          value={loading ? '—' : stats.orcPendentes}
          sub={loading ? '' : `Ticket médio: ${formatCurrency(stats.ticketMedio)}`}
        />
      </div>

      {/* Middle row */}
      <div className="dash-mid-grid">

        {/* Revenue chart */}
        <div className="dash-card dash-chart-card">
          <div className="dash-card-header">
            <div>
              <h3 className="dash-card-title">Receita Mensal</h3>
              <p className="dash-card-sub">Últimos 6 meses — baseado em pedidos</p>
            </div>
          </div>
          <div className="chart-bars">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="chart-bar-wrap">
                    <div className="chart-bar skeleton" style={{ height: `${20 + i * 12}%` }} />
                    <span className="chart-bar-label">···</span>
                  </div>
                ))
              : chartData.every((b) => b.value === 0)
                ? (
                  <p style={{ color: '#74777d', fontSize: 13, textAlign: 'center', width: '100%' }}>
                    Nenhum pedido registrado ainda.
                  </p>
                )
              : chartData.map((bar, i) => (
                  <div key={i} className="chart-bar-wrap" title={`${bar.label}: ${formatCurrency(bar.value)}`}>
                    <span className="chart-bar-value">{bar.value > 0 ? formatCurrency(bar.value) : ''}</span>
                    <div
                      className="chart-bar"
                      style={{
                        height: `${bar.height}%`,
                        background: bar.current ? '#cca730' : '#1B263B',
                        opacity: bar.current ? 1 : 0.55,
                      }}
                    />
                    <span className="chart-bar-label" style={{ fontWeight: bar.current ? 700 : 400 }}>{bar.label}</span>
                  </div>
                ))
            }
          </div>
          {(!loading && !chartData.every((b) => b.value === 0)) && (
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: '#74777d' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#cca730', display: 'inline-block' }} />
                Mês atual
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#1B263B', opacity: 0.55, display: 'inline-block' }} />
                Meses anteriores
              </span>
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="dash-card dash-activity-card">
          <h3 className="dash-card-title">Atividade Recente</h3>
          <div className="activity-list">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`activity-item${i === 3 ? ' last' : ''}`}>
                  <div className="activity-icon skeleton" style={{ minWidth: 32, height: 32, borderRadius: '50%' }} />
                  <div className="activity-body" style={{ flex: 1 }}>
                    <div className="skeleton" style={{ height: 12, width: '60%', marginBottom: 6, borderRadius: 4 }} />
                    <div className="skeleton" style={{ height: 10, width: '40%', borderRadius: 4 }} />
                  </div>
                </div>
              ))
            ) : recentActivity.length === 0 ? (
              <p style={{ color: '#74777d', fontSize: 13, textAlign: 'center', padding: '1rem 0' }}>
                Nenhuma entrada registrada ainda.
              </p>
            ) : (
              recentActivity.map((item, i) => {
                const last = i === recentActivity.length - 1;
                if (item._tipo === 'entrada') return (
                  <ActivityItem key={`e-${item.id}`} icon="arrow_circle_down" iconColor="#15803d" iconBg="#dcfce7"
                    title="Entrada de estoque"
                    sub={`${item.quantidade} un.${item.fornecedor ? ` · ${item.fornecedor}` : ''}`}
                    time={formatDate(item._data)} last={last} />
                );
                if (item._tipo === 'saida') return (
                  <ActivityItem key={`s-${item.id}`} icon="arrow_circle_up" iconColor="#ba1a1a" iconBg="#ffdad6"
                    title="Saída de estoque"
                    sub={`${item.quantidade} un. · ${item.destinatario}`}
                    time={formatDate(item._data)} last={last} />
                );
                return (
                  <ActivityItem key={`p-${item.id}`} icon="shopping_cart" iconColor="#735c00" iconBg="#ffe088"
                    title={`Pedido ${item.status || 'Pendente'}`}
                    sub={`${item.cliente_nome} · ${formatCurrency(item.valor_total)}`}
                    time={formatDate(item._data)} last={last} />
                );
              })
            )}
          </div>
          <button className="activity-view-all" onClick={() => navigate('/entradas')}>
            Ver toda a atividade
            <span className="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>
      </div>

      {/* Inventory table */}
      <div className="dash-card dash-table-card">
        <div className="dash-table-header">
          <h3 className="dash-card-title">Produtos com Estoque Crítico</h3>
          <div className="dash-table-actions">
            <button className="topbar-icon-btn" onClick={fetchData}>
              <span className="material-symbols-outlined">refresh</span>
            </button>
            <button className="dash-btn-outline" onClick={() => navigate('/produtos')}>
              Ver todos
            </button>
          </div>
        </div>

        {lowStock.length === 0 && !loading ? (
          <p className="dash-empty">Nenhum produto em estoque crítico.</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Descrição</th>
                  <th className="center">Estoque</th>
                  <th className="center">Status</th>
                  <th className="center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((__, j) => (
                          <td key={j}><div className="skeleton" /></td>
                        ))}
                      </tr>
                    ))
                  : lowStock.map((p) => (
                      <tr key={p.id}>
                        <td className="td-name">{p.nome}</td>
                        <td className="td-desc">{p.descricao || '—'}</td>
                        <td className="center">
                          <div className="stock-level">
                            <div className="stock-bar">
                              <div
                                className="stock-bar-fill"
                                style={{
                                  width: `${Math.min((p.quantidade_estoque / (p.estoque_minimo || 5)) * 100, 100)}%`,
                                  background: p.quantidade_estoque === 0 ? '#ba1a1a' : '#d97706',
                                }}
                              />
                            </div>
                            <span className="stock-num" style={{ color: p.quantidade_estoque === 0 ? '#ba1a1a' : '#d97706' }}
                              title={`Mínimo: ${p.estoque_minimo ?? 5} un.`}>
                              {p.quantidade_estoque}
                            </span>
                          </div>
                        </td>
                        <td className="center">
                          {p.quantidade_estoque === 0
                            ? <span className="badge badge-error">Sem estoque</span>
                            : <span className="badge badge-warn" title={`Mínimo: ${p.estoque_minimo ?? 5}`}>
                                Crítico · mín {p.estoque_minimo ?? 5}
                              </span>
                          }
                        </td>
                        <td className="center">
                          <button
                            className="dash-btn-link"
                            onClick={() => navigate(`/produtos/editar/${p.id}`)}
                          >
                            Repor
                          </button>
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityItem({ icon, iconColor, iconBg, title, sub, time, last }) {
  return (
    <div className={`activity-item${last ? ' last' : ''}`}>
      <div className="activity-icon" style={{ background: iconBg }}>
        <span className="material-symbols-outlined" style={{ color: iconColor, fontSize: 16 }}>
          {icon}
        </span>
      </div>
      <div className="activity-body">
        <p className="activity-title">{title}</p>
        <p className="activity-sub">{sub}</p>
        <span className="activity-time">{time}</span>
      </div>
    </div>
  );
}

export default Dashboard;
